import { 
  Scan, 
  Activity, 
  FileImage, 
  Radio, 
  FileText,
  type LucideIcon 
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

export function summarizeFindings(findings: string): string[] {
  if (!findings) return [];
  
  // Remove pontos finais duplicados e divide em sentenças
  const sentences = findings
    .replace(/\.\s*\./g, '.')
    .split(/\.\s+/)
    .filter(s => s.trim().length > 0);
  
  // Filtra sentenças muito técnicas ou repetitivas
  const relevantSentences = sentences.filter(sentence => {
    const lower = sentence.toLowerCase();
    // Remove sentenças muito curtas ou muito longas
    if (sentence.length < 20 || sentence.length > 150) return false;
    // Remove sentenças que começam com jargão muito técnico
    if (lower.startsWith('técnica') || lower.startsWith('método')) return false;
    return true;
  });
  
  // Pega as 4 primeiras sentenças relevantes e trunca se necessário
  return relevantSentences
    .slice(0, 4)
    .map(s => {
      const trimmed = s.trim();
      if (trimmed.length > 80) {
        return trimmed.substring(0, 77) + '...';
      }
      return trimmed;
    });
}

export function getExamIcon(examType: string): LucideIcon {
  const type = examType.toLowerCase();
  
  if (type.includes('ressonância') || type.includes('ressonancia') || type.includes('rm')) {
    return Scan;
  }
  if (type.includes('tomografia') || type.includes('tc')) {
    return Activity;
  }
  if (type.includes('raio-x') || type.includes('raio x') || type.includes('rx')) {
    return FileImage;
  }
  if (type.includes('ultrassom') || type.includes('ecografia')) {
    return Radio;
  }
  
  return FileText;
}

export function formatRiskBadge(riskLevel: string | undefined) {
  const level = (riskLevel || 'baixo').toLowerCase();
  
  if (level.includes('alto') || level.includes('high')) {
    return { 
      variant: 'destructive' as const, 
      emoji: '🔴', 
      label: 'Alto Risco' 
    };
  }
  if (level.includes('moderado') || level.includes('médio') || level.includes('medio') || level.includes('medium')) {
    return { 
      variant: 'secondary' as const, 
      emoji: '🟡', 
      label: 'Moderado' 
    };
  }
  
  return { 
    variant: 'success' as const, 
    emoji: '🟢', 
    label: 'Baixo Risco' 
  };
}

export function summarizeConclusion(conclusion: string, maxLength = 200): string {
  if (!conclusion) return '';
  
  if (conclusion.length <= maxLength) {
    return conclusion;
  }
  
  // Tenta cortar em uma frase completa
  const cutoff = conclusion.substring(0, maxLength);
  const lastPeriod = cutoff.lastIndexOf('.');
  
  if (lastPeriod > maxLength * 0.7) {
    return conclusion.substring(0, lastPeriod + 1);
  }
  
  return cutoff.trim() + '...';
}

export function formatExamDate(date: string): string {
  if (!date) return '';
  
  try {
    const parsedDate = new Date(date);
    return format(parsedDate, "dd MMM yyyy", { locale: ptBR });
  } catch {
    return date;
  }
}
